import { useState } from "react";
import { motion } from "framer-motion";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { MapPin, Phone, Mail, Send, CheckCircle2, Clock } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const OFFICE = [43.4883306, 5.3418954];
const ADDRESS = "55 rue Pythagore, 13290 Aix-en-Provence";
const pin = L.divIcon({ className: "m2j-marker", html: '<div class="m2j-marker-pin" style="background:#E0C173;transform:scale(1.3)"></div>', iconSize: [30, 30], iconAnchor: [15, 15], popupAnchor: [0, -16] });
const EMPTY = { name: "", email: "", phone: "", profile: "cedant", message: "", website: "" };
const PROFILES = [["cedant", "Je souhaite vendre"], ["acheteur", "Je souhaite reprendre"], ["autre", "Autre demande"]];

const OfficeMap = () => (
  <div data-testid="contact-map" className="h-72 lg:h-80 border border-gray-200 overflow-hidden">
    <MapContainer center={OFFICE} zoom={15} scrollWheelZoom={false} style={{ width: "100%", height: "100%", zIndex: 0 }}>
      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
      <Marker position={OFFICE} icon={pin}><Popup><strong>PROAC Transmission</strong><br />{ADDRESS}</Popup></Marker>
    </MapContainer>
  </div>
);

const InfoLine = ({ icon: Icon, children }) => (
  <li className="flex items-start gap-3 text-[#1B2A4A]"><Icon className="w-5 h-5 text-[#E0C173] shrink-0 mt-0.5" strokeWidth={1.5} /><span>{children}</span></li>
);

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm(EMPTY);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setSending(false);
  };

  const field = (k, label, props = {}) => (
    <div>
      <Label className="text-xs uppercase tracking-wider text-[#475569]">{label}</Label>
      <Input data-testid={`contact-${k}`} value={form[k]} onChange={set(k)} className="rounded-none mt-1.5 border-gray-300 h-11" {...props} />
    </div>
  );

  return (
    <div className="bg-[#FDFDFC]">
      <Header />
      <section className="bg-[#1B2A4A] py-16">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <span className="text-[#E0C173] uppercase tracking-[0.25em] text-xs font-medium">Contact</span>
          <h1 data-testid="contact-title" className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-white mt-4">Parlons de votre projet</h1>
          <p className="text-white/75 text-base md:text-lg mt-5 max-w-2xl">Cession ou reprise : un conseiller vous répond en toute confidentialité.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-16 grid lg:grid-cols-5 gap-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-2 space-y-8">
          <div>
            <h2 className="font-display text-lg font-semibold text-[#1B2A4A]">Notre bureau</h2>
            <ul className="mt-4 space-y-3 text-sm">
              <InfoLine icon={MapPin}><span data-testid="contact-address">{ADDRESS}</span></InfoLine>
              <InfoLine icon={Phone}><a href="tel:+33661161038" className="hover:text-[#C9A85A]">06 61 16 10 38</a></InfoLine>
              <InfoLine icon={Mail}><a href="mailto:contact@proac-transmission.fr" className="hover:text-[#C9A85A]">contact@proac-transmission.fr</a></InfoLine>
              <InfoLine icon={Clock}>Du lundi au vendredi, sur rendez-vous</InfoLine>
            </ul>
          </div>
          <OfficeMap />
          <a data-testid="contact-directions" href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(ADDRESS)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-medium text-[#1B2A4A] border-b border-[#E0C173] pb-0.5 hover:text-[#C9A85A] transition-colors">
            <MapPin className="w-4 h-4" /> Itinéraire vers le bureau
          </a>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="lg:col-span-3 bg-white border border-gray-200 p-6 sm:p-10">
          {sent ? (
            <div data-testid="contact-success" className="py-16 text-center">
              <CheckCircle2 className="w-14 h-14 text-[#E0C173] mx-auto" strokeWidth={1.5} />
              <h2 className="font-display text-2xl font-semibold text-[#1B2A4A] mt-4">Message envoyé</h2>
              <p className="text-[#475569] mt-2">Merci, un conseiller PROAC Transmission vous recontacte très rapidement.</p>
              <button data-testid="contact-new" onClick={() => setSent(false)} className="mt-6 text-sm text-[#1B2A4A] border-b border-[#E0C173]">Envoyer un autre message</button>
            </div>
          ) : (
            <form data-testid="contact-form" onSubmit={submit} className="space-y-5">
              <div className="flex flex-wrap gap-2">
                {PROFILES.map(([k, label]) => (
                  <button type="button" key={k} data-testid={`contact-profile-${k}`} onClick={() => setForm({ ...form, profile: k })}
                    className={`px-4 py-2 text-sm border transition-colors ${form.profile === k ? "bg-[#1B2A4A] text-white border-[#1B2A4A]" : "border-gray-300 text-[#475569] hover:border-[#1B2A4A]"}`}>{label}</button>
                ))}
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                {field("name", "Nom et prénom *", { required: true, minLength: 2 })}
                {field("phone", "Téléphone", { type: "tel" })}
              </div>
              {field("email", "Email *", { type: "email", required: true })}
              <input type="text" name="website" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute -left-[9999px] w-px h-px opacity-0" />
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#475569]">Votre message *</Label>
                <Textarea data-testid="contact-message" required minLength={10} value={form.message} onChange={set("message")} rows={6} placeholder="Présentez brièvement votre projet (secteur, région, calendrier…)" className="rounded-none mt-1.5 border-gray-300" />
              </div>
              <p className="text-xs text-gray-500">Vos données sont utilisées uniquement pour répondre à votre demande, conformément à notre politique de confidentialité.</p>
              <button data-testid="contact-submit" type="submit" disabled={sending} className="inline-flex items-center gap-2 bg-[#E0C173] hover:bg-[#C9A85A] text-[#1B2A4A] font-medium px-8 py-3 rounded-full transition-colors disabled:opacity-50">
                <Send className="w-4 h-4" /> {sending ? "Envoi…" : "Envoyer le message"}
              </button>
            </form>
          )}
        </motion.div>
      </section>
      <Footer />
    </div>
  );
}
